import React, { useState, useEffect } from 'react';
import { FaBox, FaPlus, FaMinus, FaEdit, FaSearch, FaExclamationTriangle } from 'react-icons/fa';

const SellerInventory = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [filter, setFilter] = useState('all');

    useEffect(() => {
        // Mock data - in production, fetch from API
        setTimeout(() => {
            setProducts([
                { id: 'PRD001', name: 'Handwoven Silk Saree', sku: 'SAR-001', stock: 15, lowStockThreshold: 5, price: 4599, status: 'in_stock' },
                { id: 'PRD002', name: 'Brass Decorative Lamp', sku: 'LMP-012', stock: 3, lowStockThreshold: 5, price: 1899, status: 'low_stock' },
                { id: 'PRD003', name: 'Pattachitra Painting', sku: 'ART-008', stock: 8, lowStockThreshold: 3, price: 2499, status: 'in_stock' },
                { id: 'PRD004', name: 'Silver Ankle Bracelet', sku: 'JWL-023', stock: 0, lowStockThreshold: 5, price: 1299, status: 'out_of_stock' },
                { id: 'PRD005', name: 'Terracotta Vase Set', sku: 'DEC-015', stock: 12, lowStockThreshold: 5, price: 899, status: 'in_stock' },
            ]);
            setLoading(false);
        }, 500);
    }, []);

    const getStockStatus = (product) => {
        if (product.stock === 0) return { label: 'Out of Stock', color: 'bg-red-100 text-red-800' };
        if (product.stock <= product.lowStockThreshold) return { label: 'Low Stock', color: 'bg-yellow-100 text-yellow-800' };
        return { label: 'In Stock', color: 'bg-green-100 text-green-800' };
    };

    const updateStock = (id, change) => {
        setProducts(products.map(p => {
            if (p.id === id) {
                const newStock = Math.max(0, p.stock + change);
                let newStatus = 'in_stock';
                if (newStock === 0) newStatus = 'out_of_stock';
                else if (newStock <= p.lowStockThreshold) newStatus = 'low_stock';
                return { ...p, stock: newStock, status: newStatus };
            }
            return p;
        }));
    };

    const filteredProducts = products.filter(p => {
        const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            p.sku.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesFilter = filter === 'all' || p.status === filter;
        return matchesSearch && matchesFilter;
    });

    const lowStockCount = products.filter(p => p.status === 'low_stock').length;
    const outOfStockCount = products.filter(p => p.status === 'out_of_stock').length;

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Inventory Management</h1>
                <p className="text-gray-600">Track and manage your product stock levels</p>
            </div>

            {/* Alerts */}
            {(lowStockCount > 0 || outOfStockCount > 0) && (
                <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-3">
                    <FaExclamationTriangle className="text-yellow-600" />
                    <span className="text-yellow-800">
                        {lowStockCount > 0 && `${lowStockCount} product(s) low on stock. `}
                        {outOfStockCount > 0 && `${outOfStockCount} product(s) out of stock.`}
                    </span>
                </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
                    <p className="text-gray-500 text-sm">Total Products</p>
                    <p className="text-2xl font-bold text-gray-800">{products.length}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
                    <p className="text-gray-500 text-sm">In Stock</p>
                    <p className="text-2xl font-bold text-gray-800">{products.filter(p => p.status === 'in_stock').length}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-500">
                    <p className="text-gray-500 text-sm">Low Stock</p>
                    <p className="text-2xl font-bold text-gray-800">{lowStockCount}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow border-l-4 border-red-500">
                    <p className="text-gray-500 text-sm">Out of Stock</p>
                    <p className="text-2xl font-bold text-gray-800">{outOfStockCount}</p>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
                <div className="flex flex-wrap gap-4 items-center justify-between">
                    <div className="flex gap-2">
                        {['all', 'in_stock', 'low_stock', 'out_of_stock'].map((status) => (
                            <button
                                key={status}
                                onClick={() => setFilter(status)}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${filter === status
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                            >
                                {status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                            </button>
                        ))}
                    </div>
                    <div className="relative">
                        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search products..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </div>
            </div>

            {/* Inventory Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-gray-500">Loading inventory...</div>
                ) : (
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {filteredProducts.map((product) => {
                                const status = getStockStatus(product);
                                return (
                                    <tr key={product.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">{product.name}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-gray-500">{product.sku}</td>
                                        <td className="px-6 py-4 whitespace-nowrap font-medium">₹{product.price.toLocaleString()}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => updateStock(product.id, -1)}
                                                    className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                                                    disabled={product.stock === 0}
                                                >
                                                    <FaMinus size={12} />
                                                </button>
                                                <span className="w-12 text-center font-semibold">{product.stock}</span>
                                                <button
                                                    onClick={() => updateStock(product.id, 1)}
                                                    className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                                                >
                                                    <FaPlus size={12} />
                                                </button>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                                                {status.label}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <button className="p-2 text-blue-600 hover:bg-blue-50 rounded">
                                                <FaEdit />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default SellerInventory;
